import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, MessageCircle, Phone, Store, ShieldCheck } from 'lucide-react';
import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { messageUrl } from '../lib/whatsapp';
import { ProductGallery, ProductCard, specRows, price, conditionBadge } from '../components/vitrin/ProductParts';

const API = `${RUNTIME_BACKEND_URL}/api/hesap/public`;
const SOLD = { satildi: 'Satıldı', rezerve: 'Rezerve' };

export default function PublicCihazPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/cihaz/${id}`).then(async r => {
      if (!r.ok) throw new Error('Ürün bulunamadı');
      const d = await r.json();
      setItem(d);
      document.title = `${d.marka} ${d.model} - ${d.seller?.name || ''}`;
      const list = await fetch(`${API}/cihazlar/${d.tenant_id}`).then(x => x.json()).catch(() => []);
      setOthers(list.filter(x => x.id !== d.id).slice(0, 8));
    }).catch(e => setError(e.message));
  }, [id]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="public-product-error">{error}</div>;
  if (!item) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const seller = item.seller || {};
  const custom = seller.custom_categories || [];
  const { cat, rows } = specRows(item, custom);
  const [cond, condCls] = conditionBadge(item.durum);
  const sold = SOLD[item.stok_durumu];
  const wa = messageUrl('whatsapp', seller.phone, `Merhaba, ${item.marka} ${item.model} (${price(item.satis_fiyati)}) ürünü hakkında bilgi almak istiyorum: ${window.location.href}`);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="public-product-page">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to={`/vitrin/${item.tenant_id}`} className="flex items-center gap-2 font-bold" data-testid="product-store-link"><Store className="h-5 w-5 text-emerald-600" /> {seller.name || 'Vitrin'}</Link>
          <Link to={`/vitrin/${item.tenant_id}`} className="text-sm text-slate-500 hover:text-slate-900">Tüm ürünler →</Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-10 grid lg:grid-cols-2 gap-8">
        <ProductGallery images={item.resimler} />
        <div className="space-y-5">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${condCls}`}>{cond}</span>
              <span className="text-xs font-medium px-3 py-1 rounded-full bg-slate-200 text-slate-700">{cat.label}</span>
              {sold && <span className="text-xs font-bold px-3 py-1 rounded-full bg-red-500 text-white">{sold}</span>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="product-title">{item.marka} {item.model}</h1>
            <p className={`text-3xl font-bold mt-3 ${sold ? 'text-slate-400 line-through' : 'text-emerald-600'}`} data-testid="product-price">{price(item.satis_fiyati)}</p>
          </div>
          {!sold && wa && (
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={wa} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold rounded-2xl py-3.5" data-testid="product-whatsapp-btn"><MessageCircle className="h-5 w-5" /> WhatsApp'tan Sor / Satın Al</a>
              <a href={`tel:${seller.phone}`} className="inline-flex items-center justify-center gap-2 border bg-white font-semibold rounded-2xl px-6 py-3.5" data-testid="product-call-btn"><Phone className="h-5 w-5" /> Ara</a>
            </div>
          )}
          {rows.length > 0 && (
            <div className="bg-white rounded-2xl border overflow-hidden" data-testid="product-specs">
              <p className="px-5 py-3 font-semibold border-b">Ürün Özellikleri</p>
              {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 px-5 py-2.5 text-sm border-b last:border-0"><span className="text-slate-500">{k}</span><span className="font-medium text-right">{v}</span></div>)}
            </div>
          )}
          {(item.ozellikler || item.aciklama) && (
            <div className="bg-white rounded-2xl border p-5 text-sm text-slate-700 whitespace-pre-line">{[item.ozellikler, item.aciklama].filter(Boolean).join('\n\n')}</div>
          )}
          <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Satıcı: {seller.name}{seller.address ? ` · ${seller.address}` : ''}</div>
        </div>
      </main>
      {others.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-12">
          <h2 className="text-xl font-bold mb-4">Satıcının diğer ürünleri</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{others.map(o => <ProductCard key={o.id} item={o} custom={custom} />)}</div>
        </section>
      )}
    </div>
  );
}
